import * as z from "zod";
import { UseFormReturn } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { H4 } from "@/components/ui/h4.tsx";
import {
  Tooltip,
  TooltipTrigger,
  TooltipProvider,
  TooltipContent,
} from "@/components/ui/tooltip";
import { InfoIcon, Bot } from "lucide-react";
import { BadgeInput } from "@/components/ui/badgeInput";
import { Button } from "@/components/ui/button";

const arrayWithoutEmptyStrings = z
  .array(z.string())
  .transform((array) => array.filter((value) => value !== ""));

export const additionalBackgroundFormSchema = z.object({
  bio: z.string(),
  style: z.string(),
  target_audience: arrayWithoutEmptyStrings,
  persona_objective: z.string(),
  occupation: z.string(),
});

export type AdditionalBackground = z.infer<
  typeof additionalBackgroundFormSchema
>;

type AdditionalBackgroundStepProps = {
  readOnly: boolean;
  form: UseFormReturn<AdditionalBackground>;
  onQuickCreateClick?: () => void;
};

export function AdditionalBackgroundStep({
  readOnly,
  form,
  onQuickCreateClick,
}: AdditionalBackgroundStepProps) {
  return (
    <Form {...form}>
      <form className="flex flex-col gap-y-1 w-full">
        <TooltipProvider delayDuration={0}>
          <div className="flex justify-between items-center mb-2">
            <H4 className="text-primary">Craft Your AI's Identity</H4>
            {onQuickCreateClick && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onQuickCreateClick}
                disabled={readOnly}
              >
                <Bot className="mr-2 h-4 w-4" />
                AI Quick Create
              </Button>
            )}
          </div>
          <p className="text-sm text-white mt-0 mb-5 align-top">
            Shape your AI's character through detailed traits, interests, and
            objectives. From writing style to target audience, each element you
            define brings your AI to life - crafting a unique digital
            personality that resonates with its intended audience.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <FormField
              control={form.control}
              name="bio"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Bio</FormLabel>
                  <FormControl>
                    <Textarea
                      autoFocus
                      rows={4}
                      disabled={readOnly}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-y-4 w-full">
              <FormField
                control={form.control}
                name="style"
                render={({ field }) => (
                  <FormItem>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex flex-row items-center w-max">
                          <FormLabel>Style</FormLabel>
                          <InfoIcon className="pl-1 w-4" />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-64">
                          Details about the agent's style. This can include
                          thinking style, spoken style (tone).
                        </p>
                      </TooltipContent>
                    </Tooltip>
                    <FormControl>
                      <Input disabled={readOnly} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="target_audience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Audience (comma-separated)</FormLabel>
                    <FormControl>
                      <BadgeInput
                        value={field.value}
                        onChange={(newTags) => field.onChange(newTags)}
                        placeholder="Add an audience target"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="persona_objective"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Agent Objective</FormLabel>
                    <FormControl>
                      <Input disabled={readOnly} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="occupation"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Occupation</FormLabel>
                    <FormControl>
                      <Input disabled={readOnly} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>
        </TooltipProvider>
      </form>
    </Form>
  );
}
